/**
 * ProfileMeta — inline row of profile metadata (creator type, pronouns, location,
 * website, extra links, birthday when shown, joined date).
 *
 * Pure presentational: User in, JSX out. No hooks or stores.
 * Each item is only rendered when its backing field is present.
 */
import styled from 'styled-components';
import { Icon } from '@/icons';
import type { User } from '@/core/models';
import { formatBirthday, isWebAddress, linkIcon, linkText } from '@/core/lib/profileFields';

/* ── Styled components ──────────────────────────────────────────────────── */

const MetaRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: ${({ theme }) => theme.lineHeight.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const MetaItem = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const MetaLink = styled.a`
  min-width: 0;
  color: ${({ theme }) => theme.color.brandFrom};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/* ── Component ──────────────────────────────────────────────────────────── */

export interface ProfileMetaProps {
  user: User;
}

export function ProfileMeta({ user }: ProfileMetaProps) {
  const hasLocation = Boolean(user.location);
  const hasLink = Boolean(user.link);
  const hasJoined = Boolean(user.joined);
  // 1.24.00 B2 — "Joined through @handle's invite". `invitedBy` is in the
  // PRIVATE block of serialize_user, so it is present only on the member's own
  // payload: rendering whenever it exists IS "own view only", by construction.
  const invitedBy = user.invitedBy ?? null;
  // 1.27.70 Edit Profile fields. Links are re-checked here even though the
  // server only stores http(s): a profile renders them as links to other people.
  const links = (user.links ?? []).filter((l) => isWebAddress(l.href));
  const birthday = user.birthdayPublic;

  if (
    !hasLocation &&
    !hasLink &&
    !hasJoined &&
    !invitedBy &&
    !user.creatorType &&
    !user.pronouns &&
    links.length === 0 &&
    !birthday
  ) {
    return null;
  }

  return (
    <MetaRoot>
      {user.creatorType && (
        <MetaItem>
          <Icon name="briefcase" size={15} />
          {user.creatorType}
        </MetaItem>
      )}

      {user.pronouns && (
        <MetaItem>
          <Icon name="user" size={15} />
          {user.pronouns}
        </MetaItem>
      )}

      {hasLocation && (
        <MetaItem>
          <Icon name="map-pin" size={15} />
          {user.location}
        </MetaItem>
      )}

      {hasLink && user.link && (
        <MetaItem>
          <Icon name="link" size={15} />
          <MetaLink href={user.link.href} target="_blank" rel="noopener noreferrer">
            {user.link.label}
          </MetaLink>
        </MetaItem>
      )}

      {links.map((l) => (
        <MetaItem key={`${l.href}-${l.label}`}>
          <Icon name={linkIcon(l.href)} size={15} />
          <MetaLink href={l.href} target="_blank" rel="noopener noreferrer me">
            {linkText(l)}
          </MetaLink>
        </MetaItem>
      ))}

      {birthday && (
        <MetaItem>
          <Icon name="cake" size={15} />
          Born {formatBirthday(birthday)}
        </MetaItem>
      )}

      {hasJoined && (
        <MetaItem>
          <Icon name="calendar-clock" size={15} />
          Joined {user.joined}
        </MetaItem>
      )}

      {invitedBy && (
        <MetaItem data-testid="invited-by">
          <Icon name="user-plus" size={15} />
          Joined through {invitedBy.handle}&apos;s invite
        </MetaItem>
      )}
    </MetaRoot>
  );
}
